/**
 * C.12 — NIPE taskbar tray icon.
 *
 * Lives next to the clock in the Win95 taskbar. Click opens the NIPE
 * chat panel; right-click cycles hide/show via the prefs mutation.
 *
 * The icon is a tiny emoji paperclip for now. A sprite-sheet thumb
 * would be nicer but the Clippy map.png frame is 124×93 which is too
 * large for a 24×24 tray slot.
 */

import { useCallback, type ReactElement } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { useFeatureFlags } from '../../lib/tenant/useFeatureFlags';
import { useResolvedPortalSlug } from '../../lib/tenant/portalSlugFromUrl';
import { useShowcaseSessionId } from '../../lib/showcase/showcaseSession';
import { useShowcaseVisible } from '../../lib/showcase/useShowcaseVisible';
import { testIds } from '../../lib/testIds';
import { useNipeControl } from './NipeControlContext';

export function NipeTrayIcon(): ReactElement | null {
  const flags = useFeatureFlags();
  const [isDemo] = useShowcaseVisible();
  const showcaseSessionId = useShowcaseSessionId();
  const nipe = useNipeControl();

  const clientSlug = useResolvedPortalSlug() ?? '';
  const prefs = useQuery(
    api.nipePrefs.getPrefs,
    flags.nipeEnabled && clientSlug
      ? { clientSlug, ...(isDemo ? { demoSessionId: showcaseSessionId } : {}) }
      : 'skip',
  );
  const setPrefs = useMutation(api.nipePrefs.setPrefs);
  const logEvent = useMutation(api.nipeAuditLog.logEvent);

  const hidden = prefs?.hidden ?? false;

  const toggleHidden = useCallback(
    (next: boolean): void => {
      if (!clientSlug) return;
      void setPrefs({
        clientSlug,
        hidden: next,
        ...(isDemo ? { demoSessionId: showcaseSessionId } : {}),
      }).catch(() => undefined);
      void logEvent({
        clientSlug,
        event: 'hide',
        detail: next ? 'hidden' : 'visible',
        ...(isDemo ? { demoSessionId: showcaseSessionId } : {}),
      }).catch(() => undefined);
    },
    [clientSlug, showcaseSessionId, isDemo, setPrefs, logEvent],
  );

  if (!flags.nipeEnabled || !clientSlug) return null;

  return (
    <button
      type="button"
      data-testid={testIds.nipe.trayIcon}
      onClick={() => {
        if (hidden) {
          toggleHidden(false);
          nipe.open();
        } else {
          nipe.toggle();
        }
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        toggleHidden(!hidden);
      }}
      title={hidden ? 'Show NIPE' : 'NIPE — right-click to hide'}
      style={{
        flexShrink: 0,
        width: 28,
        height: 28,
        padding: 0,
        marginRight: 4,
        background: hidden ? '#a0a0a0' : '#ffe',
        borderStyle: 'solid',
        borderWidth: 2,
        borderTopColor: '#808080',
        borderRightColor: '#dfdfdf',
        borderBottomColor: '#dfdfdf',
        borderLeftColor: '#808080',
        cursor: 'pointer',
        fontSize: 16,
        lineHeight: '20px',
        color: '#000',
        userSelect: 'none',
      }}
    >
      📎
    </button>
  );
}
